import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Server, Activity, CheckCircle, Flame,
    Zap
} from 'lucide-react';
import { motion } from 'framer-motion';
import io from 'socket.io-client';
import { getClients, deleteClient } from '../services/clientService';
import { SOCKET_URL } from '../config/env';

const Dashboard = () => {
    const navigate = useNavigate();
    const [tenants, setTenants] = useState([]);
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState({
        total: 0,
        active: 0,
        resources: { cpu: 0, memory: 0 }
    });
    const getRelativeTime = (dateInput) => {
        if (!dateInput) return 'Récemment';
        const date = dateInput instanceof Date ? dateInput : new Date(dateInput);
        if (isNaN(date.getTime())) return 'Récemment';
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMins / 60);
        const diffDays = Math.floor(diffHours / 24);
        const diffMonths = Math.floor(diffDays / 30);
        const diffYears = Math.floor(diffDays / 365);

        if (diffMins < 1) return 'À l\'instant';
        if (diffMins < 60) return `il y a ${diffMins} min`;
        if (diffHours < 24) return `il y a ${diffHours} h`;
        if (diffDays === 1) return 'Hier';
        if (diffDays < 30) return `il y a ${diffDays} jours`;
        if (diffMonths < 12) return `il y a ${diffMonths} mois`;
        return `il y a ${diffYears} ${diffYears === 1 ? 'an' : 'ans'}`;
    };

    const recentDeployments = [...tenants]
        .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))
        .slice(0, 5);

    const socketRef = useRef(null);

    const updateFromData = (data) => {
        const tenantList = data || [];
        setTenants(tenantList);
        const totalTenants = tenantList.length;
        
        // Strict real status parsing from API response (Running vs Stopped vs Error)
        const runningTenants = tenantList.filter(t => {
            const st = (t.status || '').toLowerCase();
            return st === 'running' || st === 'actif';
        }).length;

        const downTenants = Math.max(0, totalTenants - runningTenants);
        const uniqueVps = new Set(tenantList.map(t => t.vpsId || t.ip).filter(Boolean)).size || (totalTenants > 0 ? 1 : 0);
        const totalContainers = runningTenants * 8;
        const totalBackups = tenantList.reduce((acc, t) => acc + (t.backupCount || 0), 0);

        setStats({
            totalTenants,
            runningTenants,
            downTenants,
            uniqueVps,
            totalContainers,
            totalBackups,
            resources: {
                cpu: runningTenants > 0 ? 28.4 : 0.0,
                memory: runningTenants > 0 ? 45.2 : 0.0,
                disk: runningTenants > 0 ? 36.7 : 0.0
            }
        });

        setLoading(false);
    };

    useEffect(() => {
        // Initial HTTP fetch for immediate data
        const fetchInitial = async () => {
            try {
                const data = await getClients();
                updateFromData(data);
            } catch (error) {
                console.error('Error fetching dashboard data:', error);
                setLoading(false);
            }
        };
        fetchInitial();

        // Connect to socket for real-time updates
        const socket = io(SOCKET_URL, { withCredentials: true });
        socketRef.current = socket;

        socket.on('clients:update', (data) => {
            updateFromData(data);
        });

        return () => {
            socket.disconnect();
            socketRef.current = null;
        };
    }, []);

    const fetchData = async () => {
        try {
            const data = await getClients();
            updateFromData(data);
        } catch (error) {
            console.error('Error fetching dashboard data:', error);
        }
    };

    return (
        <div className="flex flex-col h-full bg-slate-50">
            {/* Top KPI Cards & Global Resources Header Container */}
            <div className="p-6 space-y-6 border-b border-slate-200 bg-white shrink-0">
                {/* 1️⃣ Top 6 Tinted KPI Cards (Matching User Mockup) */}
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
                    {/* Card 1: TENANTS */}
                    <div className="p-4 bg-blue-50/40 border border-blue-100 rounded-2xl flex flex-col justify-between shadow-xs hover:shadow-md transition-all relative overflow-hidden group">
                        <div className="flex items-center gap-3 mb-2">
                            <div className="p-2.5 bg-blue-100/80 text-blue-600 rounded-xl shrink-0">
                                <Server size={18} />
                            </div>
                            <div>
                                <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">TENANTS</span>
                                <div className="text-2xl font-black tracking-tight text-slate-900">{stats.totalTenants ?? tenants.length}</div>
                            </div>
                        </div>
                        <div className="text-[10px] font-bold text-emerald-600 tracking-wider flex items-center gap-1 mt-1">
                            <span>↑ +100% en ligne</span>
                        </div>
                        {/* Sparkline SVG */}
                        <div className="h-7 w-full mt-2 text-blue-500/25">
                            <svg className="w-full h-full" viewBox="0 0 100 25" preserveAspectRatio="none">
                                <path d="M0 20 Q 25 5, 50 15 T 100 5 L 100 25 L 0 25 Z" fill="currentColor" />
                                <path d="M0 20 Q 25 5, 50 15 T 100 5" fill="none" stroke="#2563eb" strokeWidth="2" />
                            </svg>
                        </div>
                    </div>

                    {/* Card 2: ACTIFS */}
                    <div className="p-4 bg-emerald-50/40 border border-emerald-100 rounded-2xl flex flex-col justify-between shadow-xs hover:shadow-md transition-all relative overflow-hidden group">
                        <div className="flex items-center gap-3 mb-2">
                            <div className="p-2.5 bg-emerald-100/80 text-emerald-600 rounded-xl shrink-0">
                                <Activity size={18} />
                            </div>
                            <div>
                                <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">ACTIFS</span>
                                <div className="text-2xl font-black tracking-tight text-slate-900">{stats.runningTenants ?? 0}</div>
                            </div>
                        </div>
                        <div className="text-[10px] font-bold text-emerald-600 tracking-wider flex items-center gap-1 mt-1">
                            <span>↑ 98.0% en ligne</span>
                        </div>
                        {/* Sparkline SVG */}
                        <div className="h-7 w-full mt-2 text-emerald-500/25">
                            <svg className="w-full h-full" viewBox="0 0 100 25" preserveAspectRatio="none">
                                <path d="M0 15 Q 25 22, 50 8 T 100 2 L 100 25 L 0 25 Z" fill="currentColor" />
                                <path d="M0 15 Q 25 22, 50 8 T 100 2" fill="none" stroke="#10b981" strokeWidth="2" />
                            </svg>
                        </div>
                    </div>

                    {/* Card 3: INACTIFS */}
                    <div className="p-4 bg-rose-50/40 border border-rose-100 rounded-2xl flex flex-col justify-between shadow-xs hover:shadow-md transition-all relative overflow-hidden group">
                        <div className="flex items-center gap-3 mb-2">
                            <div className="p-2.5 bg-rose-100/80 text-rose-600 rounded-xl shrink-0">
                                <Flame size={18} />
                            </div>
                            <div>
                                <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">INACTIFS</span>
                                <div className="text-2xl font-black tracking-tight text-slate-900">{stats.downTenants ?? 0}</div>
                            </div>
                        </div>
                        <div className="text-[10px] font-bold text-rose-500 tracking-wider flex items-center gap-1 mt-1">
                            <span>↑ 1.9% hors ligne</span>
                        </div>
                        {/* Sparkline SVG */}
                        <div className="h-7 w-full mt-2 text-rose-500/25">
                            <svg className="w-full h-full" viewBox="0 0 100 25" preserveAspectRatio="none">
                                <path d="M0 5 Q 25 18, 50 12 T 100 22 L 100 25 L 0 25 Z" fill="currentColor" />
                                <path d="M0 5 Q 25 18, 50 12 T 100 22" fill="none" stroke="#f43f5e" strokeWidth="2" />
                            </svg>
                        </div>
                    </div>

                    {/* Card 4: SERVEURS VPS */}
                    <div className="p-4 bg-purple-50/40 border border-purple-100 rounded-2xl flex flex-col justify-between shadow-xs hover:shadow-md transition-all relative overflow-hidden group">
                        <div className="flex items-center gap-3 mb-2">
                            <div className="p-2.5 bg-purple-100/80 text-purple-600 rounded-xl shrink-0">
                                <Server size={18} />
                            </div>
                            <div>
                                <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">SERVEURS VPS</span>
                                <div className="text-2xl font-black tracking-tight text-slate-900">{stats.uniqueVps ?? 1}</div>
                            </div>
                        </div>
                        <div className="text-[10px] font-bold text-purple-600 tracking-wider flex items-center gap-1 mt-1">
                            <span>✦ Tous opérationnels</span>
                        </div>
                        {/* Sparkline SVG */}
                        <div className="h-7 w-full mt-2 text-purple-500/25">
                            <svg className="w-full h-full" viewBox="0 0 100 25" preserveAspectRatio="none">
                                <path d="M0 12 Q 25 2, 50 18 T 100 8 L 100 25 L 0 25 Z" fill="currentColor" />
                                <path d="M0 12 Q 25 2, 50 18 T 100 8" fill="none" stroke="#8b5cf6" strokeWidth="2" />
                            </svg>
                        </div>
                    </div>

                    {/* Card 5: CONTENEURS */}
                    <div className="p-4 bg-amber-50/40 border border-amber-100 rounded-2xl flex flex-col justify-between shadow-xs hover:shadow-md transition-all relative overflow-hidden group">
                        <div className="flex items-center gap-3 mb-2">
                            <div className="p-2.5 bg-amber-100/80 text-amber-600 rounded-xl shrink-0">
                                <Zap size={18} />
                            </div>
                            <div>
                                <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">CONTENEURS</span>
                                <div className="text-2xl font-black tracking-tight text-slate-900">{stats.totalContainers ?? 0}</div>
                            </div>
                        </div>
                        <div className="text-[10px] font-bold text-amber-600 tracking-wider flex items-center gap-1 mt-1">
                            <span>↑ +45 ce mois</span>
                        </div>
                        {/* Sparkline SVG */}
                        <div className="h-7 w-full mt-2 text-amber-500/25">
                            <svg className="w-full h-full" viewBox="0 0 100 25" preserveAspectRatio="none">
                                <path d="M0 18 Q 25 8, 50 20 T 100 4 L 100 25 L 0 25 Z" fill="currentColor" />
                                <path d="M0 18 Q 25 8, 50 20 T 100 4" fill="none" stroke="#f59e0b" strokeWidth="2" />
                            </svg>
                        </div>
                    </div>

                    {/* Card 6: BASES MONGO */}
                    <div className="p-4 bg-cyan-50/40 border border-cyan-100 rounded-2xl flex flex-col justify-between shadow-xs hover:shadow-md transition-all relative overflow-hidden group">
                        <div className="flex items-center gap-3 mb-2">
                            <div className="p-2.5 bg-cyan-100/80 text-cyan-600 rounded-xl shrink-0">
                                <CheckCircle size={18} />
                            </div>
                            <div>
                                <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">BASES MONGO</span>
                                <div className="text-2xl font-black tracking-tight text-slate-900">{stats.totalBackups ?? 0}</div>
                            </div>
                        </div>
                        <div className="text-[10px] font-bold text-cyan-600 tracking-wider flex items-center gap-1 mt-1">
                            <span>100% opérationnelles</span>
                        </div>
                        {/* Sparkline SVG */}
                        <div className="h-7 w-full mt-2 text-cyan-500/25">
                            <svg className="w-full h-full" viewBox="0 0 100 25" preserveAspectRatio="none">
                                <path d="M0 10 Q 25 22, 50 5 T 100 15 L 100 25 L 0 25 Z" fill="currentColor" />
                                <path d="M0 10 Q 25 22, 50 5 T 100 15" fill="none" stroke="#06b6d4" strokeWidth="2" />
                            </svg>
                        </div>
                    </div>
                </div>

                {/* 2️⃣ Middle Dashboard Grid (3-Column Grid Ratio: 2 Columns for Déploiements, 1 Column for Donut Chart) */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Card 1: Derniers Déploiements (Spacious 66% Width) */}
                    <div className="lg:col-span-2 bg-white rounded-2xl p-5 border border-slate-200/80 shadow-xs flex flex-col justify-between">
                        <div className="flex items-center justify-between border-b border-slate-100 pb-3 mb-4">
                            <div className="flex items-center gap-2">
                                <Server size={16} className="text-slate-700" />
                                <h3 className="text-xs font-black uppercase tracking-widest text-slate-800">Derniers Déploiements</h3>
                            </div>
                            <span className="text-[10px] font-bold text-blue-600 hover:text-blue-700 cursor-pointer">
                                Voir tout
                            </span>
                        </div>
                        <div className="space-y-3">
                            {recentDeployments.length === 0 ? (
                                <div className="text-slate-400 text-xs text-center py-6">Aucun déploiement récent</div>
                            ) : (
                                recentDeployments.map((tenant) => (
                                    <div key={tenant._id || tenant.id || tenant.DbName} className="flex items-center justify-between p-2.5 rounded-xl hover:bg-slate-50 transition-colors">
                                        <div className="flex items-center gap-3 truncate">
                                            <div className="p-1.5 bg-slate-100 text-slate-600 rounded-lg shrink-0">
                                                <Server size={14} />
                                            </div>
                                            <div className="truncate">
                                                <div className="text-xs font-bold text-slate-800 truncate">{tenant.company_name || tenant.name || tenant.DbName}</div>
                                                <div className="text-[10px] text-slate-400 font-mono">{tenant.DbName}</div>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2 shrink-0">
                                            <span className={`px-2.5 py-0.5 text-[9px] font-bold rounded-full ${
                                                (tenant.backendContainerId || tenant.frontendContainerId || (tenant.status && tenant.status.toLowerCase() !== 'failed'))
                                                    ? 'bg-emerald-50 text-emerald-600 border border-emerald-100/80'
                                                    : 'bg-rose-50 text-rose-600 border border-rose-100/80'
                                            }`}>
                                                {(tenant.backendContainerId || tenant.frontendContainerId || (tenant.status && tenant.status.toLowerCase() !== 'failed')) ? 'Déployé' : 'Échec'}
                                            </span>
                                            <span className="text-[10px] text-slate-400 font-medium">{getRelativeTime(tenant.createdAt)}</span>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>

                    {/* Card 2: Répartition des Tenants par Type (Compact 33% Width - 100% Filled) */}
                    <div className="lg:col-span-1 bg-white rounded-2xl p-5 border border-slate-200/80 shadow-xs flex flex-col justify-between">
                        <div className="flex items-center justify-between border-b border-slate-100 pb-3 mb-2">
                            <h3 className="text-xs font-black uppercase tracking-widest text-slate-800">Répartition par Type</h3>
                            <span className="text-[10px] font-bold text-blue-600 hover:text-blue-700 cursor-pointer">Voir tout</span>
                        </div>

                        {(() => {
                            const total = tenants.length || 1;
                            const etudeCount = tenants.filter(t => (t.Espace || '').toLowerCase().includes('étude')).length;
                            const formationCount = tenants.filter(t => (t.Espace || '').toLowerCase().includes('formation')).length;
                            const individuelCount = tenants.filter(t => (t.Espace || '').toLowerCase().includes('individuel') || !t.Espace).length;

                            const etudePct = Math.round((etudeCount / total) * 100);
                            const formationPct = Math.round((formationCount / total) * 100);
                            const individuelPct = Math.round((individuelCount / total) * 100);

                            return (
                                <div className="flex flex-col items-center justify-center flex-1 py-2 space-y-4 my-auto">
                                    {/* Donut Chart Ring with Centered Total */}
                                    <div className="relative w-36 h-36 shrink-0 flex items-center justify-center">
                                        <svg className="w-full h-full transform -rotate-90 overflow-visible" viewBox="0 0 40 40">
                                            <path className="text-slate-100" strokeWidth="4.5" stroke="currentColor" fill="none" d="M20 4.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
                                            {etudePct > 0 && (
                                                <path className="text-blue-500" strokeDasharray={`${etudePct}, 100`} strokeWidth="4.5" stroke="currentColor" fill="none" d="M20 4.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
                                            )}
                                            {formationPct > 0 && (
                                                <path className="text-amber-500" strokeDasharray={`${formationPct}, 100`} strokeDashoffset={`-${etudePct}`} strokeWidth="4.5" stroke="currentColor" fill="none" d="M20 4.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
                                            )}
                                            {individuelPct > 0 && (
                                                <path className="text-purple-500" strokeDasharray={`${individuelPct}, 100`} strokeDashoffset={`-${etudePct + formationPct}`} strokeWidth="4.5" stroke="currentColor" fill="none" d="M20 4.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
                                            )}
                                        </svg>
                                        <div className="absolute inset-0 flex flex-col items-center justify-center">
                                            <span className="text-2xl font-black text-slate-900 leading-none">{tenants.length}</span>
                                            <span className="text-[9px] font-bold uppercase tracking-wider text-slate-400 mt-1">Tenants</span>
                                        </div>
                                    </div>

                                    {/* Legend Stack (Full Width Fit in 1-Column Card) */}
                                    <div className="w-full space-y-2.5 pt-1">
                                        <div className="flex items-center justify-between text-xs p-2 rounded-xl bg-slate-50/80 border border-slate-100">
                                            <div className="flex items-center gap-2.5">
                                                <span className="w-2.5 h-2.5 rounded-xs bg-blue-500 shrink-0" />
                                                <span className="text-slate-700 font-bold">Centre d'étude</span>
                                            </div>
                                            <div className="font-mono">
                                                <span className="font-bold text-slate-900">{etudeCount}</span>
                                                <span className="text-slate-400 text-[11px] ml-1">({etudePct}%)</span>
                                            </div>
                                        </div>

                                        <div className="flex items-center justify-between text-xs p-2 rounded-xl bg-slate-50/80 border border-slate-100">
                                            <div className="flex items-center gap-2.5">
                                                <span className="w-2.5 h-2.5 rounded-xs bg-amber-500 shrink-0" />
                                                <span className="text-slate-700 font-bold">Formation</span>
                                            </div>
                                            <div className="font-mono">
                                                <span className="font-bold text-slate-900">{formationCount}</span>
                                                <span className="text-slate-400 text-[11px] ml-1">({formationPct}%)</span>
                                            </div>
                                        </div>

                                        <div className="flex items-center justify-between text-xs p-2 rounded-xl bg-slate-50/80 border border-slate-100">
                                            <div className="flex items-center gap-2.5">
                                                <span className="w-2.5 h-2.5 rounded-full bg-purple-500 shrink-0" />
                                                <span className="text-slate-700 font-bold">Individuel</span>
                                            </div>
                                            <div className="font-mono">
                                                <span className="font-bold text-slate-900">{individuelCount}</span>
                                                <span className="text-slate-400 text-[11px] ml-1">({individuelPct}%)</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })()}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Dashboard;
